import { ReactElement, useState } from 'react'
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import type { ShearAnchorPosition } from '@api/fastAPI.schemas'
import { MaterialSymbol } from '@ui2/icons'

export type ExportedFilter = 'all' | 'exported' | 'not-exported'

/** The Schubanker list's position filter: the anchors at the wall's foot, at its head, or all. */
export type ShearAnchorPositionFilter = 'all' | ShearAnchorPosition

export type ElementListFilters = {
  /** Matches against element label / id. */
  label: string
  /** Selected cross-section (`qs`) values; empty means all. */
  crossSections: string[]
  /** Filter by the exported (`ok`) flag. */
  exported: ExportedFilter
  /** Filter by the row's `shearAnchorPosition` (Schubanker list only). */
  shearAnchorPosition: ShearAnchorPositionFilter
}

export const EMPTY_ELEMENT_LIST_FILTERS: ElementListFilters = {
  label: '',
  crossSections: [],
  exported: 'all',
  shearAnchorPosition: 'all',
}

export const countActiveElementListFilters = (filters: ElementListFilters): number =>
  (filters.label.trim() ? 1 : 0) +
  (filters.crossSections.length > 0 ? 1 : 0) +
  (filters.exported !== 'all' ? 1 : 0) +
  (filters.shearAnchorPosition !== 'all' ? 1 : 0)

/**
 * What the `qs` column holds, which names its filter: a cross section on the member lists, the
 * chosen anchor on the Zuganker and Schubanker lists.
 */
export type QsFilterKind = 'crossSection' | 'anchor'

const QS_FILTER_LABELS: Record<QsFilterKind, { label: string; noOptionsText: string }> = {
  crossSection: { label: 'Querschnitt', noOptionsText: 'Keine Querschnitte' },
  anchor: { label: 'Anker', noOptionsText: 'Keine Anker' },
}

export type ListFilterButtonProps = {
  filters: ElementListFilters
  onChange: (next: ElementListFilters) => void
  crossSectionOptions: string[]
  /** Hide the exported filter for compact lists without an export control. */
  showExported?: boolean
  /** Names the `qs` filter after what the column holds (default: a cross section). */
  qsFilterKind?: QsFilterKind
  /** Offer the foot/head filter of the Schubanker list, whose rows carry a position. */
  showShearAnchorPosition?: boolean
}

const labelSx = {
  fontSize: 11,
  fontWeight: 500,
  mb: 0.5,
} as const

export const ListFilterButton = ({
  filters,
  onChange,
  crossSectionOptions,
  showExported = true,
  qsFilterKind = 'crossSection',
  showShearAnchorPosition = false,
}: ListFilterButtonProps): ReactElement => {
  const theme = useTheme()
  const qsFilterLabels = QS_FILTER_LABELS[qsFilterKind]
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const activeCount = countActiveElementListFilters(filters)

  return (
    <>
      <Tooltip title="Filter" arrow disableInteractive>
        <Badge color="primary" variant="dot" invisible={activeCount === 0} overlap="circular">
          <Box
            component="button"
            type="button"
            data-cy="results2-element-list-filter-button"
            onClick={e => setAnchorEl(e.currentTarget)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              cursor: 'pointer',
              flexShrink: 0,
              background: activeCount > 0 ? 'rgba(55,132,52,0.10)' : theme.palette.common.white,
              color: activeCount > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
              '&:hover': { background: theme.palette.action.hover },
            }}
          >
            <MaterialSymbol icon="filter_alt" size={18} filled={activeCount > 0} />
          </Box>
        </Badge>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 288, p: 1.5 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Filter</Typography>
            <Button
              size="small"
              onClick={() => {
                onChange(EMPTY_ELEMENT_LIST_FILTERS)
                setAnchorEl(null)
              }}
              disabled={activeCount === 0}
              sx={{ fontSize: 11, minWidth: 0, px: 0.75, py: 0.25, textTransform: 'none' }}
            >
              Zurücksetzen
            </Button>
          </Box>

          <Box>
            <Typography sx={{ ...labelSx, color: theme.palette.text.secondary }}>
              Bezeichnung
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={filters.label}
              onChange={e => onChange({ ...filters, label: e.target.value })}
              placeholder="Name / Bezeichnung…"
              inputProps={{ 'data-cy': 'results2-element-list-filter-label' }}
              InputProps={{ sx: { fontSize: 13 } }}
            />
          </Box>

          <Box>
            <Typography sx={{ ...labelSx, color: theme.palette.text.secondary }}>
              {qsFilterLabels.label}
            </Typography>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              options={crossSectionOptions}
              value={filters.crossSections}
              onChange={(_, next) => onChange({ ...filters, crossSections: next })}
              ChipProps={{ size: 'small' }}
              noOptionsText={qsFilterLabels.noOptionsText}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={filters.crossSections.length === 0 ? 'Alle' : ''}
                  inputProps={{
                    ...params.inputProps,
                    'data-cy': 'results2-element-list-filter-cross-section',
                  }}
                />
              )}
              sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
            />
          </Box>

          {showExported ? (
            <Box>
              <Typography sx={{ ...labelSx, color: theme.palette.text.secondary }}>
                Exportiert
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={filters.exported}
                onChange={(_, next: ExportedFilter | null) => {
                  if (next) onChange({ ...filters, exported: next })
                }}
              >
                <ToggleButton value="all" sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}>
                  Alle
                </ToggleButton>
                <ToggleButton
                  value="exported"
                  sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}
                >
                  Ja
                </ToggleButton>
                <ToggleButton
                  value="not-exported"
                  sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}
                >
                  Nein
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : null}

          {showShearAnchorPosition ? (
            <Box>
              <Typography sx={{ ...labelSx, color: theme.palette.text.secondary }}>Lage</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={filters.shearAnchorPosition}
                onChange={(_, next: ShearAnchorPositionFilter | null) => {
                  if (next) onChange({ ...filters, shearAnchorPosition: next })
                }}
                data-cy="results2-element-list-filter-shear-anchor-position"
              >
                <ToggleButton value="all" sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}>
                  Alle
                </ToggleButton>
                <ToggleButton value="bottom" sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}>
                  Fuß
                </ToggleButton>
                <ToggleButton value="top" sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}>
                  Kopf
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : null}
        </Box>
      </Popover>
    </>
  )
}
